import { EXPERIMENT_NAME } from 'experiments/constants/Experiments'
import { useLogExperiment } from 'experiments/hooks/useLogExperiment'
import { useStartExperiment } from 'experiments/hooks/useStartExperiment'
import { ExperimentStateValue } from 'experiments/state/ExperimentReducer'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { useEffect, useState } from 'react'
import { FeatureFlag } from 'shared/enums/FeatureFlag'

type UseCSExperiment = {
  /** The current state of the Consumer Search experiment */
  experimentState: ExperimentStateValue
  /** Indicates if the experiment is currently being assigned */
  isAssigning: boolean
  /** Indicates if the Consumer Search Darwin experiment is enabled */
  isCSExperimentEnabled: boolean
  /** Utility variable that indicates if Consumer Search is the active search service */
  isConsumerSearchActive: boolean | undefined
  /** Logs the exposure of the Consumer Search experiment */
  logCSExposure: () => void
  /** Indicates if the assigned group differs from the group used during SSR */
  isSSRGroupDifferent: boolean | null
  /** The name of the tracking service name used for analytics */
  trackingServiceName: 'algolia' | 'consumer_search'
}

type Options = {
  /** If true, starts the experiment by assigning a group if none is set */
  shouldStartExperiment?: boolean
  /** If true, logs the experiment exposure */
  logExperiment?: boolean
  /** The CS Darwin group assigned during SSR */
  ssrCSDarwinGroup?: string
}

/**
 *   Encapsulates client-side logic for the Darwin experiment to A/B test search services.
 */
export const useCSExperiment = (options?: Options): UseCSExperiment => {
  // Default options
  const { shouldStartExperiment = false, logExperiment = false, ssrCSDarwinGroup } = options || {}

  // Get general and specific experiment FFs
  const areExperimentsEnabled = useFeatureFlag(FeatureFlag.WEB_FC_ENABLE_EXPERIMENTS)
  const isCSExperimentActive = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_CS_EXPERIMENT)
  const isCSExperimentEnabled = areExperimentsEnabled && isCSExperimentActive

  // Start the experiment (assign group) if enabled and allowed
  // OLD const consumerSearchExperiment: EXPERIMENT_NAME = 'web_fc_search_cs_algolia'
  const consumerSearchExperiment: EXPERIMENT_NAME = 'web_fc_search_bar'
  const { experimentState, isAssigning } = useStartExperiment(
    consumerSearchExperiment,
    !isCSExperimentEnabled || !shouldStartExperiment,
  )

  // Controls exposure logging
  const [enableLogExperiment, setEnableLogExperiment] = useState(logExperiment)
  const logCSExposure = () => setEnableLogExperiment(true)
  useLogExperiment(consumerSearchExperiment, !isCSExperimentEnabled || !enableLogExperiment)

  // Indicates if assigned group differs from SSR group
  const [isSSRGroupDifferent, setIsSSRGroupDifferent] = useState<boolean | null>(null)
  useEffect(() => {
    if (experimentState?.groupAssigned && !isAssigning && ssrCSDarwinGroup) {
      setIsSSRGroupDifferent(experimentState.groupAssigned !== ssrCSDarwinGroup)
    }
  }, [isAssigning, experimentState?.groupAssigned, ssrCSDarwinGroup])

  // Name of the tracking service name for analytics
  const trackingServiceName =
    isCSExperimentEnabled && experimentState?.groupAssigned === 'consumerSearch'
      ? 'consumer_search'
      : 'algolia'

  // is Consumer Search the active search service?
  const isConsumerSearchActive = (() => {
    if (isCSExperimentEnabled) {
      if (experimentState && experimentState.groupAssigned !== null) {
        return experimentState.groupAssigned === 'consumerSearch'
      }
      return // keep it undefined until we get a response.
    } else {
      return false
    }
  })()

  return {
    experimentState,
    isAssigning,
    isConsumerSearchActive,
    isCSExperimentEnabled,
    isSSRGroupDifferent,
    logCSExposure,
    trackingServiceName,
  }
}
